import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { useConnectToStoreModal } from '@/contexts/ConnectToStoreProvider';
import { storeConnect } from "@/api/auth/requests";
import { StoreConnect } from "@/api/auth/types";
import { toast } from "sonner";

const ConnectToStoreModal = () => {
  const { isOpen, closeModal } = useConnectToStoreModal();
  const [storeUrl, setStoreUrl] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsConnecting(true);
    setConnectionStatus('idle');

    try {
      const storeURLFormatted = storeUrl.includes("myshopify")
        ? storeUrl
        : `https://${storeUrl}.myshopify.com`;

      const params: StoreConnect = {
        shop_url: storeURLFormatted,
      };

      const response = await storeConnect(params);
      if (response.auth_url) {
        window.open(response.auth_url, "_self");
        setConnectionStatus('success');
        closeModal();
      } else {
        toast.error("Failed to get authentication URL.");
        setConnectionStatus('error');
      }
    } catch (error) {
      console.error("Error connecting to Shopify:", error);
      toast.error("An error occurred while connecting to Shopify.");
      setConnectionStatus('error');
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={closeModal}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Connect to Shopify</DialogTitle>
          <DialogDescription>
            Enter your Shopify store URL to connect your workspace.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleConnect}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="storeUrl" className="text-right">
                Store URL
              </Label>
              <Input
                id="storeUrl"
                value={storeUrl}
                onChange={(e) => setStoreUrl(e.target.value)}
                className="col-span-3"
                placeholder="your-store.myshopify.com"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={isConnecting} className="w-full">
              {isConnecting ? 'Connecting...' : 'Connect'}
            </Button>
          </DialogFooter>
        </form>
        {connectionStatus === 'success' && (
          <div className="flex items-center text-green-600 mt-2">
            <CheckCircle2 className="mr-2" />
            Successfully connected to Shopify store!
          </div>
        )}
        {connectionStatus === 'error' && (
          <div className="flex items-center text-red-600 mt-2">
            <AlertCircle className="mr-2" />
            Failed to connect. Please check your store URL and try again.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ConnectToStoreModal;